import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, LogOut, Settings as SettingsIcon } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth";

/**
 * Shared "account" dropdown — reused by the sidebar `UserMiniCard` and the
 * topbar account avatar so both surface the same menu (Settings, Members,
 * Account settings, Sign out). The trigger is supplied by the caller so the
 * sidebar's email-row trigger and the topbar's circular avatar can both
 * anchor the same content without each rebuilding the dropdown.
 */
export interface AccountMenuProps {
  /** The element that opens the menu. Wrapped in a `Trigger asChild`. */
  trigger: ReactNode;
  /**
   * Where the dropdown opens relative to the trigger. Sidebar trigger sits at
   * the bottom of the column → `top`; topbar avatar sits at the top → `bottom`.
   */
  side?: "top" | "bottom";
  /** Alignment of the dropdown along the trigger's axis. */
  align?: "start" | "center" | "end";
  /** Optional callback fired before any navigation/logout — closes parent
   * containers (e.g. the mobile drawer) so they don't outlive the menu. */
  onNavigate?: () => void;
}

export function AccountMenu({
  trigger,
  side = "bottom",
  align = "end",
  onNavigate,
}: AccountMenuProps) {
  const { user, logout, isSuperAdmin, hasAccountRole, roleInActiveAccount } =
    useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  const canManageAccount = hasAccountRole("ADMIN");

  const handleLogout = async () => {
    await logout();
    onNavigate?.();
    navigate("/login", { replace: true });
  };

  const go = (to: string) => () => {
    onNavigate?.();
    navigate(to);
  };

  const itemClass = cn(
    "flex min-h-[44px] cursor-pointer items-center gap-2 rounded-[var(--r-sm)] px-3 py-2 text-[13px] outline-none",
    "data-[highlighted]:bg-[var(--surface-2)]",
  );

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side={side}
          align={align}
          sideOffset={8}
          className={cn(
            "z-50 min-w-[240px] rounded-[var(--r-lg)] border border-[var(--line)] bg-[var(--surface)] p-1",
            "shadow-[var(--shadow-3)] text-[13px] text-[var(--ink)]",
          )}
        >
          {/* Identity row at the top — gives the menu obvious context so a user
              who clicked the avatar knows whose account is open. Not interactive. */}
          <div className="flex flex-col gap-0.5 px-3 py-2 text-[12px]">
            <span className="truncate font-medium text-[var(--ink)]">
              {user.email}
            </span>
            <span className="text-label">
              {isSuperAdmin ? "Super admin" : roleInActiveAccount() ?? "Signed in"}
            </span>
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-[var(--line)]" />

          <DropdownMenu.Item onSelect={go("/settings")} className={itemClass}>
            <SettingsIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden />
            Settings
          </DropdownMenu.Item>
          {/* Change password is otherwise a URL-only surface — no other chrome
              links to /change-password voluntarily (ProtectedRoute forces it). */}
          <DropdownMenu.Item
            onSelect={go("/change-password")}
            className={itemClass}
          >
            <KeyRound className="h-4 w-4" strokeWidth={1.5} aria-hidden />
            Change password
          </DropdownMenu.Item>
          {canManageAccount && (
            <>
              <DropdownMenu.Item
                onSelect={go("/account/members")}
                className={itemClass}
              >
                Members
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={go("/account/settings")}
                className={itemClass}
              >
                Account settings
              </DropdownMenu.Item>
            </>
          )}

          <DropdownMenu.Separator className="my-1 h-px bg-[var(--line)]" />
          <DropdownMenu.Item
            onSelect={handleLogout}
            data-testid="account-menu-signout"
            className={cn(itemClass, "text-[var(--neg)]")}
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} aria-hidden />
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
